import { useTranslation } from 'react-i18next';
import { PlusCircle, Check, Ban } from 'lucide-react';
import { type DebriefFact, type DebriefStage } from '@momentum/types';
import { cn } from '@/shared/utils/utils';
import { STAGE_META } from '../../gather-facts-stage/utils/stageMeta';
import { MatchPointFactRow } from './MatchPointFactRow';

interface StageSelectRowProps {
  stage: DebriefStage;
  facts: DebriefFact[];
  isPicked: boolean;
  canPick: boolean;
  onTogglePick: () => void;
}

export function StageSelectRow({
  stage,
  facts,
  isPicked,
  canPick,
  onTogglePick,
}: StageSelectRowProps) {
  const { t } = useTranslation('debriefV2');
  const meta = STAGE_META[stage];
  const Icon = meta.icon;

  const hasFacts = facts.length > 0;
  const isDisabled = !hasFacts;
  const isPickButtonDisabled = !isPicked && !canPick;

  return (
    <div
      className={cn(
        'p-5 md:p-6 transition-colors duration-200 group',
        isPicked && 'bg-[#f0f7ff]',
        !isPicked && !isDisabled && 'hover:bg-slate-50/30',
        isDisabled && 'bg-slate-50/30 cursor-not-allowed'
      )}
    >
      <div
        className={cn('flex items-center justify-between', hasFacts && 'mb-4')}
      >
        <div
          className={cn('flex items-center gap-3', isDisabled && 'opacity-60')}
        >
          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center shadow-sm shrink-0">
            <Icon className="h-4 w-4 text-slate-400" aria-hidden />
          </div>
          <h3 className="font-semibold text-slate-900 text-base leading-tight">
            {t(meta.labelKey)}
          </h3>
        </div>

        <PickButton
          isPicked={isPicked}
          isDisabled={isPickButtonDisabled}
          onClick={onTogglePick}
          pickLabel={t('matchPoints.pickButton')}
          pickedLabel={t('matchPoints.pickedLabel')}
        />
      </div>

      {hasFacts && (
        <div className="ms-[52px] space-y-1">
          {facts.map((fact, factIndex) => (
            <MatchPointFactRow
              key={`${fact.source}-${fact.link}-${factIndex}`}
              fact={fact}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PickButtonProps {
  isPicked: boolean;
  isDisabled: boolean;
  onClick: () => void;
  pickLabel: string;
  pickedLabel: string;
}

function PickButton({
  isPicked,
  isDisabled,
  onClick,
  pickLabel,
  pickedLabel,
}: PickButtonProps) {
  if (isPicked) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold text-primary hover:bg-blue-50 hover:text-button-primary-hover transition-colors active:scale-[0.98] cursor-pointer"
      >
        <Check className="h-4 w-4" aria-hidden />
        {pickedLabel}
      </button>
    );
  }

  if (isDisabled) {
    return (
      <button
        type="button"
        disabled
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-400 cursor-not-allowed"
      >
        <Ban className="h-4 w-4 text-slate-300" aria-hidden />
        {pickLabel}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 text-sm font-semibold text-slate-700 transition active:scale-[0.98] cursor-pointer"
    >
      <PlusCircle className="h-4 w-4 text-slate-400" aria-hidden />
      {pickLabel}
    </button>
  );
}
